import { useState, FormEvent, ChangeEvent } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

interface LoginPageProps {
  theme?: unknown;
}

const LoginPage = (_props: LoginPageProps) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const login = useLogin();
    const notify = useNotify();

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await login({ username: email, password });
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : "Email o contraseña inválidos";
            notify(errorMessage, { type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(180deg, #1a237e 0%, #283593 50%, #1a237e 100%)',
                padding: 2,
                position: 'relative',
            }}
        >
            <Container maxWidth="xs">
                <Box
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: 2,
                        padding: 4,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        width: '100%',
                    }}
                >
                    {/* Lock Icon */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 3,
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <LockIcon sx={{ fontSize: 32, color: '#757575' }} />
                        </Box>
                    </Box>

                    {/* Title */}
                    <Typography
                        variant="h5"
                        component="h1"
                        align="center"
                        gutterBottom
                        sx={{
                            fontWeight: 500,
                            color: '#212121',
                            mb: 4,
                        }}
                    >
                        Iniciar Sesión
                    </Typography>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                name="email"
                                type="email"
                                label="Email"
                                value={email}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                required
                                fullWidth
                                disabled={isLoading}
                                autoComplete="email"
                                autoFocus
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1,
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#bdbdbd',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#1976d2',
                                            borderWidth: '1px',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'white',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#757575',
                                        '&.Mui-focused': {
                                            color: '#1976d2',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                name="password"
                                type="password"
                                label="Contraseña"
                                value={password}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                required
                                fullWidth
                                disabled={isLoading}
                                autoComplete="current-password"
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1,
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#bdbdbd',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#1976d2',
                                            borderWidth: '1px',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'white',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#757575',
                                        '&.Mui-focused': {
                                            color: '#1976d2',
                                        },
                                    },
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={isLoading || !email || !password}
                                sx={{
                                    mt: 1,
                                    py: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: '#1976d2',
                                    textTransform: 'uppercase',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    letterSpacing: '0.5px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                        boxShadow: 'none',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                        color: 'rgba(0, 0, 0, 0.26)',
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Iniciar Sesión'
                                )}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Container>
        </Box>
    );
};

export default LoginPage;
